import { ReactElement, useEffect, useState } from "react"
import { Button } from "@wfp/ui"
import { faCloudDownload } from "@fortawesome/free-solid-svg-icons"
import moment from "moment"
import { CONTRIBUTOR_ROLE, formatDateTime, HH_MODULE } from "constants/constants"
import { permissionsHH, SurveyVisibility } from "constants/household"
import useSurveyList from "hooks/useSurveyList"
import useCommon from "hooks/useCommon"
import { useUpdateHouseholdDetailsMutation } from "services/household/survey"
import { useGetSubCategoriesQuery } from "services/household/subCategories"
import {
  useDownloadJsonSchemasByIdMutation,
  useDownloadXlsFormSchemasByIdMutation,
} from "services/household/xlsFormSchemas"
import SurveyMeta from "pages/HouseHold/SurveyMeta"
import RenderRow from "components/Shared/Property/RenderRow"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import { SurveyById, SurveyMetaData } from "interfaces/dataInterfaces"
import { ISurveyDetails } from "interfaces/survey"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { useAppSelector } from "hooks/hooksStore"
import { selectUser } from "features/common/commonSlice"

type Props = {
  survey?: SurveyById
  mode: string
  changeMode: () => void
}

const SurveyDetails = ({ survey, mode, changeMode }: Props): ReactElement | null => {
  const { showSnackBar } = useCommon()
  const [surveyData, setSurveyData] = useState<ISurveyDetails | null>(null)
  const [startDate, setStartDate] = useState<string>(survey?.surveyStartDate || "")
  const [endDate, setEndDate] = useState<string>(survey?.surveyEndDate || "")
  const [values, setValues] = useState<SurveyMetaData | undefined>(survey)
  const user = useAppSelector(selectUser)

  const { categories, attributes, modalities, phases, organizations, statuses, isAllLoaded } = useSurveyList()
  const { data: subCategories } = useGetSubCategoriesQuery(
    { categoryId: survey?.surveyCategoryId ?? 0 },
    { skip: !survey?.surveyCategoryId },
  )
  const [download] = useDownloadXlsFormSchemasByIdMutation()
  const [viewJSON] = useDownloadJsonSchemasByIdMutation()
  const [updateHouseholdDetails, { isLoading: isUpdating }] = useUpdateHouseholdDetailsMutation()

  const reset = (): void => {
    setValues(survey)
    setStartDate(survey?.surveyStartDate || "")
    setEndDate(survey?.surveyEndDate || "")
    changeMode()
  }

  const transformSurveyDetails = (data: SurveyById): ISurveyDetails => {
    return {
      surveyName: data.surveyName,
      countryName: data.countryName,
      category: categories?.find(category => Number(category.value) === data.surveyCategoryId)?.name ?? "",
      type: subCategories?.find(type => Number(type.value) === data.surveySubCategoryId)?.name ?? "",
      xlsFormName: data.xlsFormName,
      xlsFormId: data.xlsFormId,
      baseXlsFormName: data.baseXlsFormName,
      baseXlsFormId: data.baseXlsFormId,
      modality: modalities?.find(modality => Number(modality.value) === data.surveyModalityId)?.name ?? "",
      attributes:
        attributes
          ?.filter(attribute => data.surveyAttributeIds.some(id => id === Number(attribute.value)))
          ?.map(attribute => attribute.name)
          ?.join(", ") ?? "",
      status: statuses?.find(status => Number(status.value) === data.surveyAssessmentStatusId)?.name ?? "",
      phase: phases?.find(phase => Number(phase.value) === data.surveyPhaseId)?.name ?? "",
      organizations:
        organizations
          ?.filter(org => data.organizationIds.some(id => id === Number(org.value)))
          ?.map(org => org.name)
          ?.join(", ") ?? "",
      startDate: data.surveyStartDate ? moment(data.surveyStartDate).format(formatDateTime) : "",
      endDate: data.surveyEndDate ? moment(data.surveyEndDate).format(formatDateTime) : "",
      continuosMonitoring: data.surveyHouseholdIsContinuousMonitoring ? "Yes" : "No",
      createdDate: data.surveyCreateDate ? moment(data.surveyCreateDate).format(formatDateTime) : "",
      createdBy: data.userName,
      updatedDate: data.surveyUpdateDate ? moment(data.surveyUpdateDate).format(formatDateTime) : "",
      updatedBy: data.surveyUpdateBy,
      visibility: data.surveyVisibilityId ? SurveyVisibility[data.surveyVisibilityId] : "",
    }
  }

  const handleDownload = async (fileId: number, fileName: string): Promise<void> => {
    try {
      await download({ fileId, fileName }).unwrap()
    } catch (error) {
      showSnackBar("An error occurred while downloading a required file", "error")
    }
  }

  const handleViewJSON = async (id: number): Promise<void> => {
    try {
      await viewJSON({ id, module: "household" }).unwrap()
    } catch (err) {
      const error = err as FetchBaseQueryError
      showSnackBar(
        (error?.data as { message: string })?.message ?? "An error occurred while downloading a required file",
        "error",
      )
    }
  }

  const handleEdit = async (): Promise<void> => {
    if (!survey?.surveyName || survey?.surveyName.length > 50) {
      showSnackBar("Survey name field is required and must be less than 50 characters", "warning")
      return
    }
    try {
      await updateHouseholdDetails({
        ...survey,
        surveyId: Number(survey?.surveyId),
        surveyStartDate: moment(startDate)?.isValid() ? moment(startDate).format("YYYY-MM-DD") : "",
        surveyEndDate: moment(endDate)?.isValid() ? moment(endDate).format("YYYY-MM-DD") : "",
        ...values,
      }).unwrap()
      showSnackBar("Survey details edited successfully")
      reset()
    } catch (error) {
      const errorMessage =
        (error as { data: { message: string } }).data?.message ?? "An error occurred while editing survey details"
      showSnackBar(errorMessage, "error")
    }
  }

  useEffect(() => {
    if (survey && isAllLoaded && subCategories) {
      setSurveyData(transformSurveyDetails(survey))
      setStartDate(survey?.surveyStartDate || "")
      setEndDate(survey?.surveyEndDate || "")
      setValues(survey)
    }
  }, [survey, isAllLoaded, subCategories])

  if (!surveyData) return null

  return (
    <div className="p-3">
      {mode === "view" && (
        <table>
          <RenderRow label="Name" value={surveyData?.surveyName} />
          <RenderRow label="Country" value={surveyData?.countryName} />
          <RenderRow label="Category" value={surveyData?.category} />
          <RenderRow label="Type" value={surveyData?.type} />
          <RenderRow
            label="XLSForm in XLSX"
            value={surveyData?.xlsFormName}
            icon={faCloudDownload}
            action={() => handleDownload(surveyData?.xlsFormId, surveyData?.xlsFormName)}
          />
          <RenderRow
            label="XLSForm in JSON"
            value={surveyData?.xlsFormName}
            icon={faCloudDownload}
            action={() => handleViewJSON(surveyData?.xlsFormId)}
          />
          <RenderRow
            label="Base XLSForm in XLSX"
            value={surveyData?.baseXlsFormName}
            icon={faCloudDownload}
            action={() => handleDownload(surveyData?.baseXlsFormId, surveyData?.baseXlsFormName)}
          />
          <RenderRow
            label="Base XLSForm in JSON"
            value={surveyData?.baseXlsFormName}
            icon={faCloudDownload}
            action={() => handleViewJSON(surveyData?.baseXlsFormId)}
          />
          <RenderRow label="Modality" value={surveyData?.modality} />
          <RenderRow label="Attributes" value={surveyData?.attributes} />
          <RenderRow label="Status" value={surveyData?.status} />
          <RenderRow label="Phase" value={surveyData?.phase} />
          <RenderRow label="Organizations" value={surveyData?.organizations} />
          <RenderRow label="Start date" value={surveyData?.startDate} />
          <RenderRow label="End date" value={surveyData?.endDate} />
          <RenderRow label="Monitoring" value={surveyData?.continuosMonitoring} />
          <RenderRow label="Visibility" value={surveyData?.visibility} />
          <RenderRow label="Created by" value={surveyData?.createdBy} />
          <RenderRow label="Created date" value={surveyData?.createdDate} />
          <RenderRow label="Updated by" value={surveyData?.updatedBy} />
          <RenderRow label="Updated date" value={surveyData?.updatedDate} />
        </table>
      )}

      {mode === "edit" && values && (
        <SurveyMeta
          values={values}
          setValues={setValues}
          hide={["xlsFormId", "countryId", "category"]}
          startDate={startDate ? moment(startDate) : null}
          endDate={endDate ? moment(endDate) : null}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      )}

      {survey?.surveyStatusId === 1 && (
        <PermissionWrapper
          {...permissionsHH}
          feature="edit"
          exception={
            user?.roles?.find(role => role.applicationModuleId === HH_MODULE)?.roleName === CONTRIBUTOR_ROLE &&
            user?.nameid?.toLowerCase() === values?.createdBy?.toLowerCase()
          }
        >
          <div className="flex justify-between mt-4">
            <Button onClick={reset} kind={mode === "edit" ? "tertiary" : "primary"} disabled={isUpdating}>
              {mode === "edit" ? "Cancel" : "Edit"}
            </Button>

            {mode === "edit" && (
              <Button onClick={handleEdit} disabled={isUpdating}>
                Save
              </Button>
            )}
          </div>
        </PermissionWrapper>
      )}
    </div>
  )
}

export default SurveyDetails
